import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth, api } from '../App';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  ArrowLeft, DollarSign, Calendar, Users, Briefcase, MapPin,
  Loader2, Send, MessageSquare, FileText, Check, Clock, ChevronRight
} from 'lucide-react';

const CampaignDetail = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [campaign, setCampaign] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProposalDialog, setShowProposalDialog] = useState(false);
  const [showContractDialog, setShowContractDialog] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState(null);
  
  // Proposal form
  const [proposalData, setProposalData] = useState({
    pitch: '',
    proposed_rate: 0,
    deliverables: '',
    timeline: ''
  });
  
  // Contract form
  const [contractData, setContractData] = useState({
    terms: '',
    payment_amount: 0,
    milestones: []
  });
  
  const [submitting, setSubmitting] = useState(false);

  const isCreator = user?.role === 'creator';
  const isBrandOwner = campaign?.brand_id === user?.user_id;

  const fetchCampaign = useCallback(async () => {
    setLoading(true);
    try {
      const campaignRes = await api.get(`/campaigns/${campaignId}`);
      setCampaign(campaignRes.data);
      
      // If brand owner, fetch proposals
      if (campaignRes.data.brand_id === user?.user_id) {
        const proposalsRes = await api.get(`/proposals/campaign/${campaignId}`);
        setProposals(proposalsRes.data.proposals || []);
      }
    } catch (error) {
      toast.error('Failed to load campaign');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  }, [campaignId, navigate, user?.user_id]);

  useEffect(() => {
    fetchCampaign();
  }, [fetchCampaign]);

  const handleSubmitProposal = async () => {
    setSubmitting(true);
    try {
      await api.post('/proposals', {
        campaign_id: campaignId,
        ...proposalData
      });
      toast.success('Proposal submitted!');
      setShowProposalDialog(false);
      setProposalData({ pitch: '', proposed_rate: 0, deliverables: '', timeline: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit proposal');
    } finally {
      setSubmitting(false);
    }
  };

  const handleProposalAction = async (proposalId, status) => {
    try {
      await api.put(`/proposals/${proposalId}`, { status });
      toast.success(`Proposal ${status}`);
      fetchCampaign();
    } catch (error) {
      toast.error('Failed to update proposal');
    }
  };

  const handleCreateContract = async () => {
    if (!selectedProposal) return;
    setSubmitting(true);
    try {
      await api.post(`/proposals/${selectedProposal.proposal_id}/contract`, {
        proposal_id: selectedProposal.proposal_id,
        terms: contractData.terms,
        payment_amount: contractData.payment_amount,
        milestones: contractData.milestones
      });
      toast.success('Contract created and proposal accepted!');
      setShowContractDialog(false);
      setSelectedProposal(null);
      fetchCampaign();
    } catch (error) {
      toast.error('Failed to create contract');
    } finally {
      setSubmitting(false);
    }
  };

  const startMessage = async (userId) => {
    try {
      const response = await api.post('/messages/conversations', {
        participant_id: userId,
        initial_message: `Hi! I'm interested in discussing the campaign "${campaign?.title}"`
      });
      navigate(user?.role === 'creator' ? '/creator/messages' : '/brand/messages');
    } catch (error) {
      toast.error('Failed to start conversation');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!campaign) return null;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Back Button */}
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        {/* Campaign Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Badge variant={campaign.status === 'active' ? 'default' : 'secondary'}>
              {campaign.status}
            </Badge>
            <span className="text-sm text-muted-foreground">
              {campaign.applications_count} applicants
            </span>
          </div>
          
          <h1 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            {campaign.title}
          </h1>
          
          {/* Brand Info */}
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 rounded-lg bg-secondary flex items-center justify-center overflow-hidden">
              {campaign.brand?.brand_profile?.logo_url ? (
                <img src={campaign.brand.brand_profile.logo_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <Briefcase className="w-6 h-6 text-muted-foreground" />
              )}
            </div>
            <div>
              <p className="font-semibold">{campaign.brand_name || campaign.brand?.name}</p>
              <p className="text-sm text-muted-foreground">
                {campaign.brand?.brand_profile?.industry || 'Brand'}
              </p>
            </div>
            {isCreator && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => startMessage(campaign.brand_id)}
                className="ml-auto"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Message
              </Button>
            )}
          </div>
        </div>

        {/* Campaign Details */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#3AAFB9]/20 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-[#3AAFB9]" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Budget</p>
                  <p className="font-semibold">${campaign.budget_min} - ${campaign.budget_max}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Deadline</p>
                  <p className="font-semibold">
                    {campaign.deadline ? new Date(campaign.deadline).toLocaleDateString() : 'Flexible'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#E8795A]/20 flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#84CC16]" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Applicants</p>
                  <p className="font-semibold">{campaign.applications_count}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Description */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <h2 className="text-lg font-semibold mb-4">Campaign Description</h2>
            <p className="text-muted-foreground whitespace-pre-wrap">{campaign.description}</p>
          </CardContent>
        </Card>

        {/* Niches & Platforms */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <Card>
            <CardContent className="pt-6">
              <h3 className="text-sm font-medium mb-3">Target Niches</h3>
              <div className="flex flex-wrap gap-2">
                {campaign.niches?.map(niche => (
                  <Badge key={niche} variant="secondary">{niche}</Badge>
                ))}
              </div>
            </CardContent>
          </Card>
          
          {campaign.platform_requirements?.length > 0 && (
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-sm font-medium mb-3">Platform Requirements</h3>
                <div className="flex flex-wrap gap-2">
                  {campaign.platform_requirements.map(platform => (
                    <Badge key={platform} variant="outline">{platform}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Requirements */}
        {campaign.requirements && (
          <Card className="mb-6">
            <CardContent className="pt-6">
              <h2 className="text-lg font-semibold mb-4">Requirements</h2>
              <p className="text-muted-foreground whitespace-pre-wrap">{campaign.requirements}</p>
            </CardContent>
          </Card>
        )}

        {/* Creator: Apply Button */}
        {isCreator && campaign.status === 'active' && (
          <Button 
            size="lg"
            onClick={() => setShowProposalDialog(true)}
            className="w-full rounded-full btn-glow"
            data-testid="apply-btn"
          >
            <Send className="w-5 h-5 mr-2" />
            Apply to this Campaign
          </Button>
        )}

        {/* Brand Owner: Proposals Section */}
        {isBrandOwner && proposals.length > 0 && (
          <div className="mt-8">
            <h2 className="text-xl font-semibold mb-4">Proposals Received</h2>
            <div className="space-y-4">
              {proposals.map(proposal => (
                <Card key={proposal.proposal_id} className="card-hover">
                  <CardContent className="py-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-secondary overflow-hidden flex-shrink-0">
                        {proposal.creator?.picture ? (
                          <img src={proposal.creator.picture} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Users className="w-5 h-5 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <h3 
                              className="font-semibold cursor-pointer hover:text-primary"
                              onClick={() => navigate(`/profile/${proposal.creator_id}`)}
                            >
                              {proposal.creator_name}
                            </h3>
                            <Badge className={
                              proposal.status === 'pending' ? 'bg-yellow-500/20 text-yellow-500' :
                              proposal.status === 'accepted' ? 'bg-green-500/20 text-green-500' :
                              'bg-red-500/20 text-red-500'
                            }>
                              {proposal.status}
                            </Badge>
                          </div>
                          <span className="text-[#3AAFB9] font-semibold">${proposal.proposed_rate}</span>
                        </div>
                        <p className="text-muted-foreground text-sm mb-2">{proposal.pitch}</p>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          {proposal.timeline && <span>Timeline: {proposal.timeline}</span>}
                          {proposal.deliverables && <span>Deliverables: {proposal.deliverables}</span>}
                        </div>
                        
                        {proposal.status === 'pending' && (
                          <div className="flex gap-2 mt-4">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleProposalAction(proposal.proposal_id, 'rejected')}
                            >
                              Reject
                            </Button>
                            <Button 
                              size="sm"
                              onClick={() => {
                                setSelectedProposal(proposal);
                                setContractData(prev => ({ ...prev, payment_amount: proposal.proposed_rate }));
                                setShowContractDialog(true);
                              }}
                              className="bg-[#3AAFB9] hover:bg-[#3AAFB9]/90"
                            >
                              Accept & Create Contract
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Apply Dialog */}
      <Dialog open={showProposalDialog} onOpenChange={setShowProposalDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Apply to: {campaign.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-sm font-medium mb-2 block">Your Pitch *</Label>
              <Textarea
                placeholder="Why are you a great fit for this campaign?"
                value={proposalData.pitch}
                onChange={(e) => setProposalData(prev => ({ ...prev, pitch: e.target.value }))}
                rows={4}
                data-testid="pitch-input"
              />
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Proposed Rate ($) *</Label>
              <Input
                type="number"
                placeholder="Your rate for this campaign"
                value={proposalData.proposed_rate || ''}
                onChange={(e) => setProposalData(prev => ({ ...prev, proposed_rate: parseFloat(e.target.value) || 0 }))}
                data-testid="rate-input"
              />
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Deliverables</Label>
              <Input
                placeholder="e.g., 2 Instagram posts, 1 story"
                value={proposalData.deliverables}
                onChange={(e) => setProposalData(prev => ({ ...prev, deliverables: e.target.value }))}
              />
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Timeline</Label>
              <Input
                placeholder="e.g., 1 week"
                value={proposalData.timeline}
                onChange={(e) => setProposalData(prev => ({ ...prev, timeline: e.target.value }))}
              />
            </div>
            <Button 
              onClick={handleSubmitProposal}
              disabled={submitting || !proposalData.pitch || !proposalData.proposed_rate}
              className="w-full"
              data-testid="submit-proposal-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Proposal'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Contract Dialog */}
      <Dialog open={showContractDialog} onOpenChange={setShowContractDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Contract</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-4 bg-secondary/50 rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">Creator</p>
              <p className="font-medium">{selectedProposal?.creator_name}</p>
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Payment Amount ($)</Label>
              <Input
                type="number"
                value={contractData.payment_amount || ''}
                onChange={(e) => setContractData(prev => ({ ...prev, payment_amount: parseFloat(e.target.value) || 0 }))}
              />
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Contract Terms</Label>
              <Textarea
                placeholder="Define the terms of this collaboration..."
                value={contractData.terms}
                onChange={(e) => setContractData(prev => ({ ...prev, terms: e.target.value }))}
                rows={4}
              />
            </div>
            <Button 
              onClick={handleCreateContract}
              disabled={submitting || !contractData.payment_amount}
              className="w-full bg-[#3AAFB9] hover:bg-[#3AAFB9]/90"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Contract & Accept'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CampaignDetail;
